<template>
    <div class="relative flex flex-1 flex-col bg-white rounded-lg">
        
        <Fullscreen v-if="! [4, 5].includes(auction.md) && auction.s == AUCTION_LIVE_STATUS_AUCTIONING" :key="auction.b"></Fullscreen>

        <div v-if="[4, 5].includes(auction.md) && auction.s == AUCTION_LIVE_STATUS_AUCTIONING" class="flex items-center justify-center rounded-full bg-white w-10 h-10 p-2" style="position: absolute; top: 8px; right: 20px; z-index: 1001;">
            <button v-if="soundsAreMuted" @click="toggleMute" class="text-red-500">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 9.75 19.5 12m0 0 2.25 2.25M19.5 12l2.25-2.25M19.5 12l-2.25 2.25m-10.5-6 4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" />
                </svg>
            </button>
            <button v-else @click="toggleMute">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" />
                </svg>
            </button>
        </div>
    
        <Messages v-if="[4, 5].includes(auction.md) && auction.s == AUCTION_LIVE_STATUS_AUCTIONING"></Messages>
    
        <ConfirmBid v-if="[4, 5].includes(auction.md)" @confirmbid="bid" />
        
        <template v-if="auction.s == AUCTION_LIVE_STATUS_AUCTIONING">
            <div class="flex">
                <div class="flex p-4 justify-center items-center">
                    <transition name="slide-fade" mode="out-in">
                        <Paddle v-if="winner" :key="winner.w" :paddle="winner.w" bg-color="#FFF01F" text-color="#000000" width="50px" />
                        <Paddle v-else :key="0" :paddle="''" bg-color="#f1f1f1" text-color="#000000" width="50px" />
                    </transition>
                </div>
                <div class="py-4 flex flex-col flex-1 justify-center">
                    <div class="uppercase">
                        {{ $t('general.lot') }} {{ lot.no }}
                    </div>
                    <div class="text-sm">
                        <v-clamp autoresize :max-lines="4">{{ description }}</v-clamp>
                    </div>
                    <div v-if="lot.emin" class="flex flex-no-wrap text-sm text-gray-500">
                        <span class="mr-1">{{ $t('general.est') }}: </span>
                        <money-format
                            :value="lot.emin"
                            locale="ro"
                            currency-code="eur"
                            :subunits-value="false"
                            :hide-subunits="true">
                        </money-format>
                        <span class="mx-1">&ndash;</span>
                        <money-format
                            :value="lot.emax"
                            locale="ro"
                            currency-code="eur"
                            :subunits-value="false"
                            :hide-subunits="true">
                        </money-format>
                    </div>
                </div>
                <div class="flex flex-no-wrap justify-end items-center">
                    <div v-if="lot.p > 0" class="flex flex-col text-center p-4">
                        <div class="text-xs text-gray-500">{{ $t('bids.current') }} (EUR)</div>
                        <div class="text-2xl">
                            <AnimatedNumber :value="lot.p" :precision="0" />
                        </div>
                    </div>
                    <div v-if="lot.k > 0" class="flex flex-col text-center p-4">
                        <div class="text-xs text-gray-500">{{ $t('bids.ask') }} (EUR)</div>
                        <div class="text-2xl">
                            <AnimatedNumber :value="lot.k" :precision="0" />
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="lot.s == LOT_AUCTION_STATUS_OPEN" class="flex px-4 py-4 border-gray-300 border-t border-b">
                <button v-if="user.id && user.approval_status == 2" :disabled="! canBid" @mouseover="hovering(true)" @mouseleave="hovering(false)" @click="confirmbid" :class="{'bid-blink': isWarn}" class="relative w-full inline-flex items-center justify-center px-6 py-3 text-base font-medium rounded-md text-white bg-black hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black">
                    <div class="absolute z-10 bg-white top-0 bottom-0 left-0 rounded-tl-md rounded-bl-md opacity-50 transition-all" :style="{'width': lotTimer}"></div>
                    <span v-if="canBid" class="flex z-50">
                        <span v-if="lot.warn == 1">{{ $t('bids.bid_warn') }}</span>
                        <span v-else>{{ $t('bids.bid') }}</span>
                        <span class="mx-1 z-50">
                            <AnimatedNumber :value="lot.k" :precision="0" />
                        </span>
                        <span class="uppercase">eur</span>
                    </span>
                    <span v-else-if="isHighestBidder" class="flex z-50"><img class="w-6 h-6 mr-2" src="@live/assets/images/clap.svg">{{ $t('bids.highest', {'value': winner.p}) }}</a></span>
                    <span v-else-if="isBidder" class="flex z-50">{{ $t('bids.sent', {'value': lot.k}) }}</span>
                    <span v-else-if="isBidding" class="flex z-50">{{ $t('bids.is_bidding') }}</span>
                    <span v-else>{{ $t('bids.wait') }}</span>
                </button>
                <a v-else-if="user.id && user.approval_status != 2" :href="`/${locale}/login`" class="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-black hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black">
                    {{ $t('account.review') }}
                </a>
                <a v-else :href="`/${locale}/login`" class="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-black hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black">
                    {{ $t('account.login') }}
                </a>

                <!-- suspend bid more until further notice -->
                <!--
                <select v-if="this.user && this.user.id" :disabled="! canBid" v-model="moreAmount" @change="confirmBidMore" class="ml-4 inline-flex items-center justify-center bg-black text-white px-6 py-3 text-base font-medium rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black" :placeholder="$t('bids.more')">
                    <option :value="0">{{ $t('bids.more') }}</option>
                    <option v-for="step in steps" :value="step" :key="step">
                        <money-format
                            :value="step"
                            locale="ro"
                            currency-code="eur"
                            :subunits-value="false"
                            :hide-subunits="true">
                        </money-format>
                    </option>
                </select>
                -->

            </div>
            <!-- <div v-if="auction && auction.m" class="bg-gray-300 text-black flex m-4 py-2 px-4 text-center justify-center items-center rounded-lg">
                {{ auction.m }}
            </div> -->
            <div v-if="lot.s == LOT_AUCTION_STATUS_OPEN && bids.length > 0" class="flex flex-1 paddles text-black relative p-4">
                <TransitionGroup v-if="bids" name="paddles-rise" tag="div">
                    <div v-for="(bid, index) in bids.slice(0, max_paddles)" :key="index">
                        <Paddle :paddle="bid.w" width="60" height="100%" bg-color="#FFF01F" text-color="#000000" />
                    </div>
                </TransitionGroup>
                <div v-if="bidsCount > max_paddles" class="more ml-4 text-lg">+ {{ bidsCount - max_paddles }}</div>
                <div class="absolute bottom-0 right-0 p-2">
                    <img class="w-32" src="@live/assets/images/logo-20-m.svg" />
                </div>
            </div>
            <div v-else-if="lot.s == LOT_AUCTION_STATUS_OPEN && bids.length == 0 && warn" class="flex flex-1 text-black text-lg justify-center items-center relative">
                {{ $t(warn.type, {'value': warn.params}) }}
                <div class="absolute bottom-0 right-0 p-2">
                    <img class="w-32" src="@live/assets/images/logo-20-m.svg" />
                </div>
            </div>
            <div v-else-if="lot.s == LOT_AUCTION_STATUS_OPEN && bids.length == 0 && !warn" class="flex flex-1 text-black text-lg justify-center items-center relative">
                <div class="absolute bottom-0 right-0 p-2">
                    <img class="w-32" src="@live/assets/images/logo-20-m.svg" />
                </div>
            </div>
            <div v-else-if="lot.s == LOT_AUCTION_STATUS_PENDING" class="flex flex-1 text-black text-lg items-center justify-center border-gray-300 border-t relative">
                {{ $t('bids.wait') }}
                <div class="absolute bottom-0 right-0 p-2">
                    <img class="w-32" src="@live/assets/images/logo-20-m.svg" />
                </div>
            </div>
            <div v-else-if="lot.s == LOT_AUCTION_STATUS_SOLD" class="flex flex-1 flex-col text-black text-lg items-center justify-center border-gray-300 border-t relative">
                <div>{{ $t('bids.sold') }}</div><div v-if="isHighestBidder">{{ $t('bids.congrats') }}</div>
                <div class="absolute bottom-0 right-0 p-2">
                    <img class="w-32" src="@live/assets/images/logo-20-m.svg" />
                </div>
            </div>
            <div v-else-if="lot.s == LOT_AUCTION_STATUS_UNSOLD" class="flex flex-1 text-black text-lg items-center justify-center border-gray-300 border-t relative">
                {{ $t('bids.pass') }}
                <div class="absolute bottom-0 right-0 p-2">
                    <img class="w-32" src="@live/assets/images/logo-20-m.svg" />
                </div>
            </div>
            <div v-else class="flex flex-1 text-black text-lg items-center justify-center border-gray-300 border-t relative">
                {{ $t('bids.wait') }}
                <div class="absolute bottom-0 right-0 p-2">
                    <img class="w-32" src="@live/assets/images/logo-20-m.svg" />
                </div>
            </div>
        </template>
        <template v-else>
            <div class="flex flex-1 flex-col justify-center items-center">
                <div v-if="auction.m" class="flex justify-center items-center bg-red-600 w-full p-2">
                    {{ auction.m }}
                </div>
                <div class="flex flex-1 justify-center items-center p-8">
                    <img class="w-64" src="@live/assets/images/logo-20-m.svg" />
                </div>
                <div v-if="auction.n" class="flex flex-1 justify-center items-center text-center p-8">
                    {{ auction.n }}
                </div>
                <div class="flex flex-1 justify-center items-center text-center p-8">
                    <div class="text-center" v-if="auction.s == AUCTION_LIVE_STATUS_STANDBY">
                        {{ $t('general.standby') }}
                        <vue-countdown-timer
                            :start-time="auction.start"
                            :interval="1000"
                            :start-label="$t('general.standby')"
                            label-position="before"
                            :day-txt="$t('timer.days')"
                            :hour-txt="$t('timer.hours')"
                            :minutes-txt="$t('timer.minutes')"
                            :seconds-txt="$t('timer.seconds')">

                            <template slot="start-label" slot-scope="scope">
                                <span v-if="scope.props.startLabel !== '' && scope.props.tips && scope.props.labelPosition === 'begin'">{{scope.props.startLabel}}</span>
                                <span v-if="scope.props.endLabel !== '' && !scope.props.tips && scope.props.labelPosition === 'begin'">{{scope.props.endLabel}}</span>
                            </template>

                            <template slot="countdown" slot-scope="scope">
                                <template>
                                    <span>{{scope.props.days}}</span> {{scope.props.dayTxt}}
                                </template>

                                <template>
                                    <span>{{scope.props.hours}}</span> {{scope.props.hourTxt}}
                                </template>

                                <template>
                                    <span>{{scope.props.minutes}}</span> {{scope.props.minutesTxt}}
                                </template>

                                <template>
                                    <span>{{scope.props.seconds}}</span> {{scope.props.secondsTxt}}
                                </template>
                            </template>

                            <template slot="end-label" slot-scope="scope">
                                <span v-if="scope.props.startLabel !== '' && scope.props.tips && scope.props.labelPosition === 'end'">{{scope.props.startLabel}}:</span>
                                <span v-if="scope.props.endLabel !== '' && !scope.props.tips && scope.props.labelPosition === 'end'">{{scope.props.endLabel}}:</span>
                            </template>

                            <template slot="end-text" slot-scope="scope">
                                <span>{{ scope.props.endText}}</span>
                            </template>
                            
                        </vue-countdown-timer>

                        <!--
                        <div class="flex items-center justify-center py-4">
                            <audio ref="auctionloop" controls muted autoplay loop>
                                <source src="/live/sounds/auction.ogg" type="audio/ogg">
                                <source src="/live/sounds/auction.mp3" type="audio/mpeg">
                            </audio>
                        </div>
                        -->
                    </div>
                    <div class="text-center" v-if="auction.s == AUCTION_LIVE_STATUS_BREAK">
                        {{ $t('general.break') }}
                    </div>
                    <div class="text-center" v-if="auction.s == AUCTION_LIVE_STATUS_ENDED">
                        {{ $t('general.ended') }}
                    </div>
                    <div class="flex flex-col space-y-4 items-center" v-if="auction.s == AUCTION_LIVE_STATUS_HOME">
                        <span>{{ $t('general.redirect_home', { seconds: timeToRedirect }) }}</span>
                        <button @click.prevent="redirectHome(true)" type="button" class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-black text-base font-medium text-white hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                            {{ $t('general.redirect_now') }}
                        </button>
                    </div>
                    <div class="flex flex-col space-y-4 items-center" v-if="auction.s == AUCTION_LIVE_STATUS_OLD">
                        <span>{{ $t('general.redirect_old', { seconds: this.timeToRedirect }) }}</span>
                        <button @click.prevent="redirectOld(true)" type="button" class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-black text-base font-medium text-white hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                            {{ $t('general.redirect_now') }}
                        </button>
                    </div>
                    <div class="flex flex-col space-y-4 items-center" v-if="auction.s == AUCTION_LIVE_STATUS_RELOAD">
                        <span>{{ $t('general.reload_all', { seconds: this.timeToRedirect }) }}</span>
                        <button @click.prevent="reloadAll(true)" type="button" class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-black text-base font-medium text-white hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                            {{ $t('general.reload_now') }}
                        </button>
                    </div>
                </div>
            </div>
        </template>

        <BidMore :more-amount="moreAmount" @confirmbidmore="bidMore" @cancel="moreAmount = 0" />

    </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import Fullscreen from './Fullscreen'
import Paddle from '../global/Paddle'
import AnimatedNumber from '../global/AnimatedNumber'
import VClamp from 'vue-clamp'
import { Constants } from '../../constants'
import A10 from '../global/A10'
import BidMore from '../modals/BidMore'
import Messages from '../global/Messages.vue'
import ConfirmBid from '../modals/ConfirmBid'

export default {
    data () {
        return {
            ...Constants,
            max_paddles: 5,
            isBidding: false,
            moreAmount: 0,
            timeToRedirect: null,
            soundsAreMuted: false,
        }
    },
    components: {
        Fullscreen, Paddle, AnimatedNumber, VClamp, BidMore, Messages, ConfirmBid
    },
    computed: {
        ...mapGetters({
            currency: 'getCurrency',
            locale: 'getLocale',
            user: 'getUser',
            auction: 'getAuction',
            lot: 'getLot',
            selectedLot: 'getSelectedLot',
            bids: 'getBids',
            winner: 'getWinner',
            isHighestBidder: 'getIsHighestBidder',
            isWinner: 'getIsWinner',
            isBidder: 'getIsBidder',
            warn: 'getWarn',
            steps: 'getNext5Steps',
            timer: 'getTimer',
        }),
        canBid () {
            if(this.isBidding) return false
            if(this.lot.s != this.LOT_AUCTION_STATUS_OPEN) return false
            if(! this.user.id) return false
            if(this.winner && (this.winner.uid === this.user.id && this.lot.k != this.lot.r)) return false
            if(this.bids.filter(el => el.uid === this.user.id).length > 0) return false
            return true
        },
        isWarn() {
            if(this.isHighestBidder) return false
            if(this.isBidder) return false
            if(this.isBidding) return false
            if(this.lot.warn == 0) return false
            return true
        },
        description () {
            let artist = ''
            if(this.lot.a) {
                artist = this.lot.a + ', '
            }
            return artist + this.lot.t
        },
        ask () {
            return this.lot.k
        },
        auctionStatus () {
            if(! this.auction) return null
            return this.auction.s
        },
        lotTimer () {
            if(! this.timer) return 0;
            if(! this.auction) return 0;
            let ileana = this.auction.ileana
            if(! ileana) return 0;

            return (this.timer / (ileana.secondsPerLot / 10) * 100) + '%';
        },
        bidsCount() {
            if(this.bids && Array.isArray(this.bids) && this.bids.length > 0) {
                return this.bids[0].t ? this.bids[0].t : this.bids.length;
            }
            return 0;
        }
    },
    watch: {
        ask (amount) {
            if(amount >= this.moreAmount) {
                this.$modal.hide('bid-more')
            }
        },
        auctionStatus(s) {
            if(s == this.AUCTION_LIVE_STATUS_HOME) {
                this.redirectHome()
            } else if(s == this.AUCTION_LIVE_STATUS_OLD) {
                this.redirectOld()
            } else if(s == this.AUCTION_LIVE_STATUS_RELOAD) {
                this.reloadAll()
            }
        }
    },
    methods: {
        confirmbid() {
            if(this.user.live_bid_protection_status === 1) {
                this.$modal.show('confirm-bid')
            } else {
                this.bid()
            }
        },
        confirmBidMore() {
            return;

            if(this.moreAmount == 0) return
            this.$modal.show('bid-more')
        },
        async bid() {
            this.isBidding = true
            this.$modal.hide('confirm-bid')
            try {
                await this.$store.dispatch('sendBid', {
                    auction_id: this.auction.id,
                    lot_id: this.lot.id,
                    amount: this.lot.k
                })
                this.isBidding = false
            } catch (e) {
                this.isBidding = false
            }
        },
        async bidMore () {
            this.isBidding = true
            this.$modal.hide('bid-more')
            try {
                await this.$store.dispatch('sendAmount', {
                    auction_id: this.auction.id,
                    lot_id: this.lot.id,
                    amount: this.moreAmount
                })
                this.isBidding = false
            } catch (e) {
                this.isBidding = false
            }
        },
        hovering(v) {
            if(this.auction.low == true) return;
            
	        this.hover = v
            auctionPresenceChannel.whisper('h', {
                id: this.user.id,
                h: this.hover
            });
        },
        getRndInteger(min, max) {
            return Math.floor(Math.random() * (max - min) ) + min;
        },
        redirectHome (now) {
            if(now === true) {
                window.location.replace('https://www.artmark.ro')
                return;
            }

            this.timeToRedirect = this.getRndInteger(5, 30)
            window.setTimeout(() => { window.location.replace('https://www.artmark.ro') }, this.timeToRedirect * 1000)
        },
        redirectOld (now) {
            if(now === true) {
                window.location.replace('https://live.artmark.ro')
                return;
            }

            this.timeToRedirect = this.getRndInteger(5, 30)
            window.setTimeout(() => { window.location.replace('https://live.artmark.ro') }, this.timeToRedirect * 1000)
        },
        reloadAll (now) {
            if(now === true) {
                window.location.reload()
                return;
            }

            this.timeToRedirect = this.getRndInteger(5, 30)
            window.setTimeout(() => { window.location.reload() }, this.timeToRedirect * 1000)
        },
        toggleMute() {
            this.soundsAreMuted = ! this.soundsAreMuted;
            ding.mute(this.soundsAreMuted);
            outbid.mute(this.soundsAreMuted);
            highest.mute(this.soundsAreMuted);
            congrats.mute(this.soundsAreMuted);
            onetime.mute(this.soundsAreMuted);
            twice.mute(this.soundsAreMuted);
            three.mute(this.soundsAreMuted);
            lotopen.mute(this.soundsAreMuted);
            lotsold.mute(this.soundsAreMuted);
            localStorage.setItem('soundsAreMuted', this.soundsAreMuted);
        }
    },
    mounted () {
        // rate limit hover
        let last_hover = false;

        if(this.auction.s == this.AUCTION_LIVE_STATUS_HOME) {
            this.redirectHome()
        } else if(this.auction.s == this.AUCTION_LIVE_STATUS_OLD) {
            this.redirectOld()
        }

        let audioplayer = this.$refs.auctionloop;
        let t = window.setInterval(() => {
            if(audioplayer == undefined) return;

            audioplayer.play().then(() => {
                window.clearInterval(t)
            }).catch((e) => {
                
            });
        }, 1000)

        /*
        this.soundsAreMuted = (/true/).test(localStorage.getItem('soundsAreMuted'));
        ding.mute(this.soundsAreMuted);
        outbid.mute(this.soundsAreMuted);
        highest.mute(this.soundsAreMuted);
        congrats.mute(this.soundsAreMuted);
        onetime.mute(this.soundsAreMuted);
        twice.mute(this.soundsAreMuted);
        three.mute(this.soundsAreMuted);
        lotopen.mute(this.soundsAreMuted);
        lotsold.mute(this.soundsAreMuted);
        */
    }
}
</script>
